/**
 * Backend Category Service - Uses the new PostgreSQL backend API
 * This service is used when REACT_APP_USE_BACKEND_API=true
 *
 * Caching Layer: staticDataService (CDN) -> in-memory -> localStorage -> live API
 */

import { apiService } from './apiService';
import { staticDataService } from './staticDataService';

// Backend category type (may have 'active' instead of 'isActive')
interface BackendCategory {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  active?: boolean;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}

class BackendCategoryService {
  private cache: BackendCategory[] = [];
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour for performance
  private readonly STORAGE_KEY = 'categories_cache_backend';
  private pendingRequest: Promise<BackendCategory[]> | null = null;

  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.STORAGE_KEY);
      const cachedTimestamp = localStorage.getItem(`${this.STORAGE_KEY}_timestamp`);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        if (Date.now() - timestamp < this.CACHE_DURATION) {
          this.cache = JSON.parse(cachedData);
          this.cacheTimestamp = timestamp;
        }
      }
    } catch (error) {
      console.error('Error loading categories cache:', error);
    }
  }

  private saveCacheToStorage(categories: BackendCategory[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(categories));
      localStorage.setItem(`${this.STORAGE_KEY}_timestamp`, Date.now().toString());
    } catch (error) {
      console.error('Error saving categories cache:', error);
    }
  }

  constructor() {
    this.loadCacheFromStorage();
  }

  async getAllCategories(activeOnly: boolean = false, forceRefresh: boolean = false): Promise<BackendCategory[]> {
    // 1. Static CDN Cache – instant, zero backend dependency
    if (!forceRefresh) {
      const staticCats = staticDataService.getAllCategories();
      if (staticCats.length > 0) {
        if (this.cache.length === 0) {
          this.cache = staticCats as BackendCategory[];
          this.cacheTimestamp = Date.now();
          this.saveCacheToStorage(this.cache);
        }
        // SWR: silently revalidate live data in the background
        this.fetchFromBackend(activeOnly).catch(() => { });
        const filtered = activeOnly ? this.cache.filter(c => c.active !== false) : this.cache;
        return [...filtered];
      }
    }

    // 2. If we have cached data, return it immediately
    if (!forceRefresh && this.cache.length > 0) {
      const filtered = activeOnly ? this.cache.filter(c => c.active !== false) : this.cache;

      if (Date.now() - this.cacheTimestamp > 60 * 60 * 1000) {
        console.log('🔄 Cache stale (1hr+), revalidating categories in background...');
        this.fetchFromBackend(activeOnly).catch(() => { });
      }
      return [...filtered];
    }

    // Force refresh or no cache - fetch from backend
    if (forceRefresh) {
      this.clearCache();
    }

    if (this.pendingRequest) {
      const result = await this.pendingRequest;
      return activeOnly ? result.filter(c => c.active !== false) : result;
    }

    this.pendingRequest = this.fetchFromBackend(activeOnly);
    try {
      const result = await this.pendingRequest;
      this.pendingRequest = null;
      return result;
    } catch (error) {
      this.pendingRequest = null;
      if (this.cache.length === 0) {
        this.loadCacheFromStorage();
      }
      return activeOnly ? this.cache.filter(c => c.active !== false) : [...this.cache];
    }
  }

  clearCache(): void {
    this.cache = [];
    this.cacheTimestamp = 0;
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(`${this.STORAGE_KEY}_timestamp`);
  }

  private async fetchFromBackend(activeOnly: boolean): Promise<BackendCategory[]> {
    try {
      const categories = await apiService.getAllCategories(activeOnly);

      const normalizedCategories = categories.map(cat => ({
        ...cat,
        isActive: cat.isActive !== undefined ? cat.isActive : (cat.active !== undefined ? cat.active : true),
        sortOrder: cat.sortOrder !== undefined ? cat.sortOrder : (cat.sort_order !== undefined ? cat.sort_order : 0),
      }));

      this.cache = normalizedCategories;
      this.cacheTimestamp = Date.now();
      this.saveCacheToStorage(normalizedCategories);

      // Hydrate the static cache layer with fresh live data (SWR)
      staticDataService.hydrateLiveCategories(normalizedCategories);

      return normalizedCategories;
    } catch (error) {
      console.error('❌ Error fetching categories from backend:', error);
      if (this.cache.length > 0) {
        return [...this.cache];
      }
      // Last resort: return the static snapshot if available
      const staticCats = staticDataService.getAllCategories();
      if (staticCats.length > 0) return staticCats as BackendCategory[];
      throw error;
    }
  }

  async getCategoryById(id: string): Promise<BackendCategory | null> {
    // Check cache first
    const cached = this.cache.find(c => c.id === id);
    if (cached) {
      // Revalidate in background but return cache immediately
      apiService.getCategoryById(id)
        .then(cat => {
          if (cat) {
            const normalized = {
              ...cat,
              isActive: cat.isActive !== undefined ? cat.isActive : (cat.active !== undefined ? cat.active : true),
              sortOrder: cat.sortOrder !== undefined ? cat.sortOrder : (cat.sort_order !== undefined ? cat.sort_order : 0),
            };
            const idx = this.cache.findIndex(c => c.id === id);
            if (idx !== -1) {
              this.cache[idx] = normalized;
              this.saveCacheToStorage(this.cache);
            }
          }
        })
        .catch(() => { });
      return cached;
    }

    try {
      const category = await apiService.getCategoryById(id);
      if (category) {
        const normalized = {
          ...category,
          isActive: category.isActive !== undefined ? category.isActive : (category.active !== undefined ? category.active : true),
          sortOrder: category.sortOrder !== undefined ? category.sortOrder : (category.sort_order !== undefined ? category.sort_order : 0),
        };
        return normalized;
      }
      return null;
    } catch (error) {
      console.error('Error fetching category from backend:', error);
      return null;
    }
  }

  async createCategory(categoryData: {
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    active?: boolean;
  }): Promise<BackendCategory> {
    try {
      const newCategory = await apiService.createCategory(categoryData);
      // Clear cache to force refresh on next getAllCategories call
      this.cache = [];
      this.cacheTimestamp = 0;
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(`${this.STORAGE_KEY}_timestamp`);
      return {
        ...newCategory,
        isActive: newCategory.isActive !== undefined ? newCategory.isActive : (newCategory.active !== undefined ? newCategory.active : true),
        sortOrder: newCategory.sortOrder !== undefined ? newCategory.sortOrder : (newCategory.sort_order !== undefined ? newCategory.sort_order : 0),
      };
    } catch (error) {
      console.error('Error creating category in backend:', error);
      throw error;
    }
  }

  async updateCategory(id: string, updates: Partial<{
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    active?: boolean;
    sortOrder?: number;
    sort_order?: number;
  }>): Promise<BackendCategory> {
    try {
      // Map sortOrder to sort_order for backend compatibility
      const apiUpdates = { ...updates };
      if ('sortOrder' in apiUpdates && apiUpdates.sortOrder !== undefined) {
        apiUpdates.sort_order = apiUpdates.sortOrder;
        delete apiUpdates.sortOrder;
      }

      const updatedCategory = await apiService.updateCategory(id, apiUpdates);

      const normalized = {
        ...updatedCategory,
        isActive: updatedCategory.isActive !== undefined ? updatedCategory.isActive : (updatedCategory.active !== undefined ? updatedCategory.active : true),
        sortOrder: updatedCategory.sortOrder !== undefined ? updatedCategory.sortOrder : (updatedCategory.sort_order !== undefined ? updatedCategory.sort_order : 0),
      };

      // Update cache
      const cacheIndex = this.cache.findIndex(c => c.id === id);
      if (cacheIndex !== -1) {
        this.cache[cacheIndex] = normalized;
        this.saveCacheToStorage(this.cache);
      } else {
        // Clear cache to force refresh
        this.cache = [];
        this.cacheTimestamp = 0;
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.removeItem(`${this.STORAGE_KEY}_timestamp`);
      }
      return normalized;
    } catch (error) {
      console.error('Error updating category in backend:', error);
      throw error;
    }
  }

  async deleteCategory(id: string): Promise<boolean> {
    try {
      await apiService.deleteCategory(id);
      // Remove from cache
      this.cache = this.cache.filter(c => c.id !== id);
      this.saveCacheToStorage(this.cache);
      return true;
    } catch (error) {
      console.error('Error deleting category in backend:', error);
      throw error;
    }
  }
}

export const backendCategoryService = new BackendCategoryService();
export default backendCategoryService;

